// Distinct art-directed hero for the 4 macro-capability hubs.
// Brand = typography-as-art. Websites = interface frame. Marketing = poster.
// Ongoing Partner = system schematic. All render inside a full-bleed section
// with the standard editorial title + sub + CTAs below the treatment.
import React from "react";
import { Link } from "react-router-dom";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";

const CTARow = ({ slug }) => (
  <div className="mt-10 flex flex-wrap gap-3">
    <PrimaryButton to="/contact" testId={`hub-primary-${slug}`}>Start a Project</PrimaryButton>
    <SecondaryButton to="/services" testId={`hub-secondary-${slug}`}>Explore all services</SecondaryButton>
  </div>
);

const CrumbBar = ({ label }) => (
  <div className="max-w-[1440px] mx-auto px-5 lg:px-10 pt-8 lg:pt-10">
    <Crumbs items={[{ to: "/", label: "Home" }, { to: "/services", label: "Services" }, { label }]} />
  </div>
);

// ---------------- 1. BRAND — typography as the artwork ----------------
const BrandHero = ({ page }) => (
  <section data-testid="hub-hero-brand" className="relative bg-black text-white border-b border-white/10 overflow-hidden">
    <CrumbBar label={page.h1} />

    <div className="relative max-w-[1440px] mx-auto px-5 lg:px-10 py-10 lg:py-16">
      {/* Corner specimen marks */}
      <div className="hidden lg:flex absolute top-6 right-10 items-center gap-4 text-[11px] uppercase tracking-[0.32em] font-bold text-white/45">
        <span>Poppins 900</span>
        <span aria-hidden>·</span>
        <span>Kerning −5</span>
        <span aria-hidden>·</span>
        <span>Baseline 24</span>
      </div>

      <Overline className="mb-6">{page.eyebrow}</Overline>

      {/* Enormous letterform stack (art directed) */}
      <div className="relative select-none" aria-hidden="true">
        <div
          className="font-black leading-[0.82] tracking-[-0.06em] text-white"
          style={{ fontSize: "clamp(88px, 20vw, 320px)" }}
        >
          Brand.
        </div>
        <div
          className="font-black leading-[0.82] tracking-[-0.06em] text-white/[0.08]"
          style={{ fontSize: "clamp(88px, 20vw, 320px)", marginTop: "-0.12em" }}
        >
          Brand.
        </div>
        <div
          className="absolute inset-y-0 right-0 flex items-center pointer-events-none"
          style={{ transform: "translateX(6%)" }}
        >
          <span
            className="font-black leading-[0.82] tracking-[-0.06em] text-[#FFC600]"
            style={{ fontSize: "clamp(88px, 20vw, 320px)" }}
          >
            .
          </span>
        </div>
      </div>

      {/* Real H1 (visually smaller than the display letterforms) */}
      <h1 className="sr-only">{page.h1}</h1>
      <p className="mt-10 lg:mt-14 text-xl lg:text-3xl font-semibold tracking-[-0.01em] leading-[1.25] max-w-3xl text-white">
        {page.sub}
      </p>

      {/* Specimen strip */}
      <div className="mt-12 grid grid-cols-3 lg:grid-cols-6 gap-4 border-t border-white/15 pt-6 text-white/60">
        {["Aa", "Bb", "01", "Cc", "◆", "K"].map((g, i) => (
          <div key={g + i} className="border border-white/10 aspect-square flex items-center justify-center text-3xl lg:text-5xl font-black">
            {g}
          </div>
        ))}
      </div>

      <CTARow slug="brand" />
    </div>
  </section>
);

// ---------------- 2. WESTITES — browser chrome interface frame ----------------
const WebsitesHero = ({ page }) => (
  <section data-testid="hub-hero-websites" className="relative bg-black text-white border-b border-white/10">
    <CrumbBar label={page.h1} />

    <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-10 lg:py-16">
      <Overline className="mb-6">{page.eyebrow}</Overline>

      {/* Browser window frame */}
      <div className="relative border-2 border-white/20 bg-[#0A0A0A]">
        <div className="flex items-center gap-4 border-b-2 border-white/20 px-4 lg:px-5 py-3 lg:py-3.5">
          <div className="flex gap-1.5">
            <span className="block w-3 h-3 rounded-full bg-white/25" />
            <span className="block w-3 h-3 rounded-full bg-white/25" />
            <span className="block w-3 h-3 rounded-full bg-[#FFC600]" />
          </div>
          <div className="hidden md:flex flex-1 items-center gap-2 border border-white/15 bg-black px-3 py-1.5 text-[11px] font-mono text-white/60">
            <span className="text-[#FFC600]">▲</span>
            <span className="tracking-tight">kostaja.ca/services/websites</span>
            <span className="ml-auto text-white/30">200 OK · 42ms</span>
          </div>
          <div className="hidden lg:flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] font-bold text-white/45">
            <span>Mobile 76</span>
            <span aria-hidden>·</span>
            <span>Desktop 98</span>
            <span aria-hidden>·</span>
            <span>SEO 100</span>
          </div>
        </div>

        <div className="p-6 lg:p-14">
          <h1 className="text-5xl sm:text-6xl lg:text-[104px] font-black tracking-[-0.03em] leading-[0.9]">
            {page.h1}
          </h1>
          <p className="mt-6 lg:mt-8 text-lg lg:text-2xl font-semibold tracking-[-0.01em] leading-[1.3] max-w-3xl text-white/85">
            {page.sub}
          </p>

          {/* Fake viewport of a site being built */}
          <div className="mt-10 lg:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6" aria-hidden="true">
            <div className="lg:col-span-8 border border-white/15 bg-black">
              <div className="border-b border-white/10 px-4 py-2.5 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-white/50">
                <span className="w-1.5 h-1.5 bg-[#FFC600]" />
                <span>Home · draft v3</span>
              </div>
              <div className="p-5 lg:p-8 space-y-3">
                <div className="h-2.5 bg-white/70 w-3/4" />
                <div className="h-2.5 bg-white/40 w-1/2" />
                <div className="h-2.5 bg-white/20 w-2/3" />
                <div className="mt-5 h-24 lg:h-32 bg-white/[0.06] border border-white/10" />
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <div className="h-14 lg:h-16 bg-white/[0.06] border border-white/10" />
                  <div className="h-14 lg:h-16 bg-[#FFC600]" />
                  <div className="h-14 lg:h-16 bg-white/[0.06] border border-white/10" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-4 border border-white/15 bg-black">
              <div className="border-b border-white/10 px-4 py-2.5 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-white/50">
                <span className="w-1.5 h-1.5 bg-white/60" />
                <span>Service page</span>
              </div>
              <div className="p-5 space-y-2.5">
                <div className="h-2 bg-white/50 w-3/4" />
                <div className="h-2 bg-white/30 w-full" />
                <div className="h-2 bg-white/30 w-5/6" />
                <div className="h-2 bg-white/30 w-2/3" />
                <div className="mt-4 h-8 bg-[#FFC600] w-2/3" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <CTARow slug="websites" />
    </div>
  </section>
);

// ---------------- 3. MARKETING — poster / campaign energy ----------------
const MarketingHero = ({ page }) => (
  <section data-testid="hub-hero-marketing" className="relative bg-black text-white border-b border-white/10 overflow-hidden">
    <CrumbBar label={page.h1} />

    <div className="relative max-w-[1440px] mx-auto px-5 lg:px-10 py-10 lg:py-16">
      {/* Diagonal yellow tag */}
      <div className="absolute top-8 right-4 lg:top-14 lg:right-10 rotate-[6deg]">
        <div className="bg-[#FFC600] text-black font-black tracking-[-0.02em] px-6 py-3 border-2 border-black shadow-[6px_6px_0_#FFC600]">
          <div className="text-[10px] uppercase tracking-[0.32em] font-black">Now Live</div>
          <div className="text-3xl lg:text-4xl leading-none mt-1">CAMPAIGN 26</div>
        </div>
      </div>

      <Overline className="mb-6">{page.eyebrow}</Overline>

      {/* Big poster type */}
      <h1
        className="font-black tracking-[-0.05em] leading-[0.82]"
        style={{ fontSize: "clamp(88px, 18vw, 260px)" }}
      >
        <span className="block text-white">Marketing</span>
        <span className="block text-[#FFC600]">that lands.</span>
      </h1>

      <p className="mt-10 text-lg lg:text-2xl font-semibold tracking-[-0.01em] leading-[1.3] max-w-3xl text-white/85">
        {page.sub}
      </p>

      {/* Ticker strip */}
      <div className="mt-12 border-y-2 border-[#FFC600] py-4 flex items-center gap-8 overflow-hidden text-[#FFC600] font-black tracking-[0.02em] whitespace-nowrap">
        {["LOCAL SEARCH", "◆", "GOOGLE PROFILE", "◆", "SOCIAL RHYTHM", "◆", "PRINT & SIGNAGE", "◆", "CONTENT SYSTEM", "◆", "AI ANSWER ENGINES", "◆", "LOCAL SEARCH·2", "◆·2", "GOOGLE PROFILE·2"].map((t) => (
          <span key={t} className="text-sm lg:text-base uppercase">{t.replace("·2", "")}</span>
        ))}
      </div>

      <CTARow slug="marketing" />
    </div>
  </section>
);

// ---------------- 4. ONGOING PARTNER — system schematic ----------------
const OngoingPartnerHero = ({ page }) => {
  // Node coords are in the SVG viewBox (100 wide, 72 tall) for the rects.
  // `above` controls whether the HTML text label sits above or below the rect.
  const nodes = [
    { label: "Brand", x: 8, y: 30, above: true },
    { label: "Website", x: 34, y: 12, above: true },
    { label: "Marketing", x: 60, y: 30, above: true },
    { label: "Studio Lead", x: 34, y: 62, above: false, highlight: true },
    { label: "Specialists", x: 86, y: 62, above: false },
  ];
  const edges = [
    [0, 1], [1, 2], [0, 3], [3, 2], [2, 4], [3, 4], [1, 3],
  ];
  return (
    <section data-testid="hub-hero-ongoing-partner" className="relative bg-black text-white border-b border-white/10 overflow-hidden">
      <CrumbBar label={page.h1} />

      {/* Schematic layer */}
      <div className="relative max-w-[1440px] mx-auto px-5 lg:px-10 py-10 lg:py-16">
        <Overline className="mb-6">{page.eyebrow}</Overline>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <h1 className="text-5xl sm:text-6xl lg:text-[104px] font-black tracking-[-0.03em] leading-[0.9]">
              {page.h1}.
            </h1>
            <p className="mt-6 text-lg lg:text-2xl font-semibold tracking-[-0.01em] leading-[1.3] max-w-2xl text-white/85">
              {page.sub}
            </p>
            <CTARow slug="ongoing-partner" />
          </div>

          {/* Wire schematic */}
          <div className="lg:col-span-5">
            <div className="border-2 border-white/20 bg-[#0A0A0A] p-5 lg:p-6">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.28em] font-bold text-white/45 mb-4">
                <span>System View · v1.0</span>
                <span className="text-[#FFC600]">One accountable partner</span>
              </div>

              <div className="relative w-full" style={{ aspectRatio: "1 / 0.72" }}>
                <svg
                  viewBox="0 0 100 72"
                  preserveAspectRatio="none"
                  className="absolute inset-0 w-full h-full"
                  aria-hidden="true"
                >
                  <defs>
                    <pattern id="hp-grid" width="4" height="4" patternUnits="userSpaceOnUse">
                      <path d="M0 0 L0 4 M0 0 L4 0" stroke="rgba(255,255,255,0.05)" strokeWidth="0.15" />
                    </pattern>
                  </defs>
                  <rect x="0" y="0" width="100" height="72" fill="url(#hp-grid)" />
                  {edges.map(([a, b]) => (
                    <line
                      key={`edge-${a}-${b}`}
                      x1={nodes[a].x + 6}
                      y1={nodes[a].y + 4}
                      x2={nodes[b].x + 6}
                      y2={nodes[b].y + 4}
                      stroke="rgba(255,255,255,0.35)"
                      strokeWidth="0.35"
                      strokeDasharray="1.2 0.8"
                    />
                  ))}
                  {nodes.map((n, i) => (
                    <g key={n.label}>
                      <rect
                        x={n.x}
                        y={n.y}
                        width="12"
                        height="8"
                        fill={i === 3 ? "#FFC600" : "#000"}
                        stroke={i === 3 ? "#FFC600" : "rgba(255,255,255,0.55)"}
                        strokeWidth="0.35"
                      />
                    </g>
                  ))}
                </svg>
                {/* Text overlay */}
                <div className="absolute inset-0">
                  {nodes.map((n) => {
                    const style = n.above
                      ? { left: `${n.x - 1}%`, top: `${n.y - 5}%`, width: "14%" }
                      : { left: `${n.x - 1}%`, top: `${n.y + 8.5}%`, width: "14%" };
                    return (
                      <div
                        key={n.label}
                        className={`absolute text-[10px] lg:text-[11px] font-black tracking-[0.14em] uppercase text-center ${
                          n.highlight ? "text-[#FFC600]" : "text-white/80"
                        }`}
                        style={{ ...style, lineHeight: 1.1 }}
                      >
                        {n.label}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-white/60">
                <div className="border border-white/15 p-2 text-center">Monthly scope</div>
                <div className="border border-white/15 p-2 text-center">Quarterly reset</div>
                <div className="border border-[#FFC600] p-2 text-center text-[#FFC600]">One point of contact</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const HubHero = ({ page }) => {
  switch (page.slug) {
    case "brand":
      return <BrandHero page={page} />;
    case "websites":
      return <WebsitesHero page={page} />;
    case "marketing":
      return <MarketingHero page={page} />;
    case "ongoing-partner":
      return <OngoingPartnerHero page={page} />;
    default:
      return null;
  }
};

export const HUB_SLUGS = new Set(["brand", "websites", "marketing", "ongoing-partner"]);
export default HubHero;
